<script setup>
import { ref, computed, onBeforeUnmount, nextTick } from 'vue';

const props = defineProps({
  modelValue: { type: [String, Number], default: '' },
  options: {
    type: Array,
    required: true,
    // [{ value, label, hint?, icon?, dot? (tailwind bg color class) }]
  },
  icon: { type: String, default: '' },
  placeholder: { type: String, default: 'Selecionar' },
  minWidth: { type: String, default: '160px' },
});
const emit = defineEmits(['update:modelValue']);

const open = ref(false);
const rootRef = ref(null);

const selected = computed(
  () => props.options.find(o => o.value === props.modelValue) || null
);

function onClickOutside(e) {
  if (rootRef.value && !rootRef.value.contains(e.target)) {
    open.value = false;
    document.removeEventListener('mousedown', onClickOutside);
  }
}
function toggle() {
  open.value = !open.value;
  if (open.value) nextTick(() => document.addEventListener('mousedown', onClickOutside));
  else document.removeEventListener('mousedown', onClickOutside);
}
function pick(value) {
  emit('update:modelValue', value);
  open.value = false;
  document.removeEventListener('mousedown', onClickOutside);
}

onBeforeUnmount(() => document.removeEventListener('mousedown', onClickOutside));
</script>

<template>
  <div ref="rootRef" class="relative inline-block">
    <button
      type="button"
      :class="[
        'inline-flex h-9 items-center gap-1.5 rounded-lg border border-n-strong/40 bg-n-background px-3 text-xs font-medium text-n-slate-12 transition hover:border-n-strong hover:bg-n-slate-2',
        open && 'border-blue-400 ring-1 ring-blue-200',
      ]"
      :style="`min-width: ${minWidth}`"
      @click="toggle"
    >
      <span v-if="icon" :class="icon" class="size-3.5 text-n-slate-11" />
      <span
        v-if="selected?.dot"
        :class="[selected.dot, 'inline-block size-2 shrink-0 rounded-full']"
      />
      <span class="flex-1 truncate text-left">
        {{ selected?.label || placeholder }}
      </span>
      <span
        :class="['i-lucide-chevron-down size-3.5 text-n-slate-11 transition', open && 'rotate-180']"
      />
    </button>

    <div
      v-if="open"
      class="absolute left-0 top-full z-40 mt-1 max-h-80 w-auto min-w-full overflow-y-auto rounded-lg border border-n-strong/40 bg-n-background py-1 shadow-lg"
    >
      <button
        v-for="o in options"
        :key="`${o.value}`"
        type="button"
        :class="[
          'flex w-full items-start gap-2 px-3 py-2 text-left text-xs transition',
          o.value === modelValue
            ? 'bg-blue-50 text-blue-800'
            : 'text-n-slate-12 hover:bg-n-slate-2',
        ]"
        @click="pick(o.value)"
      >
        <span
          v-if="o.dot"
          :class="[o.dot, 'mt-0.5 inline-block size-2 shrink-0 rounded-full']"
        />
        <span v-if="o.icon" :class="o.icon" class="mt-0.5 size-3.5 shrink-0 text-n-slate-11" />
        <span class="min-w-0 flex-1">
          <span class="block truncate font-medium">{{ o.label }}</span>
          <span v-if="o.hint" class="block truncate text-[10px] text-n-slate-11">
            {{ o.hint }}
          </span>
        </span>
        <span
          v-if="o.value === modelValue"
          class="i-lucide-check mt-0.5 size-3.5 shrink-0 text-blue-600"
        />
      </button>
    </div>
  </div>
</template>
